(() => {
  const instances = new Map();

  class AWHeader {
    constructor(root) {
      this.root = root;
      this.desktopMedia = window.matchMedia('(min-width: 768px)');
      this.mainHeader = root.querySelector('[data-aw-header-main]');
      this.stickyHeader = root.querySelector('[data-aw-header-sticky]');
      this.mainNavList = this.mainHeader?.querySelector('.aw-header__nav-list') || null;
      this.stickyNavList = this.stickyHeader?.querySelector('.aw-header__nav-list') || null;
      this.navItems = Array.from(root.querySelectorAll('[data-aw-nav-item]'));
      this.triggers = Array.from(root.querySelectorAll('[data-aw-header-trigger]'));
      this.panels = Array.from(root.querySelectorAll('[data-aw-mega-panel]'));
      this.searchTriggers = Array.from(root.querySelectorAll('[data-aw-header-search-trigger]'));
      this.searchPanel = root.querySelector('[data-aw-header-search-panel]');
      this.drawer = root.querySelector('[data-aw-header-drawer]');
      this.drawerOpenButtons = Array.from(root.querySelectorAll('[data-aw-header-drawer-open]'));
      this.drawerCloseButtons = Array.from(root.querySelectorAll('[data-aw-header-drawer-close]'));
      this.drawerPanelButtons = Array.from(root.querySelectorAll('[data-aw-drawer-open]'));
      this.drawerBackButtons = Array.from(root.querySelectorAll('[data-aw-drawer-back]'));
      this.drawerPanels = Array.from(root.querySelectorAll('[data-aw-drawer-panel]'));
      this.activeMegaTarget = null;
      this.drawerOpen = false;
      this.announcementHeight = 0;
      this.lastScrollY = window.scrollY;
      this.scrollTicking = false;
      this.scrollStopTimer = null;
      this.searchCloseTimer = null;
      this.boundOnRootMouseLeave = this.onRootMouseLeave.bind(this);
      this.boundOnDocumentClick = this.onDocumentClick.bind(this);
      this.boundOnDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.boundOnScroll = this.onScroll.bind(this);
      this.boundOnResize = this.onResize.bind(this);
      this.boundOnMediaChange = this.onMediaChange.bind(this);
      this.boundHandleSearchResume = this.handleSearchResume.bind(this);
      this.boundHandleSearchPause = this.clearSearchCloseTimer.bind(this);
    }

    init() {
      this.root.classList.add('is-ready');
      this.bindDesktopMega();
      this.bindSearch();
      this.bindDrawer();
      this.bindGlobalEvents();
      this.measure();
      this.updateMegaWidth();
      this.updateSticky(true);
      this.resetDrawerPanels();
    }

    destroy() {
      this.navItems.forEach((item) => {
        item.removeEventListener('mouseenter', item.__awNavEnter);
        item.removeEventListener('focusin', item.__awNavEnter);
      });

      this.searchTriggers.forEach((trigger) => {
        trigger.removeEventListener('mouseenter', trigger.__awSearchEnter);
        trigger.removeEventListener('mouseleave', trigger.__awSearchLeave);
        trigger.removeEventListener('focusin', trigger.__awSearchFocusIn);
        trigger.removeEventListener('focusout', trigger.__awSearchFocusOut);
        trigger.removeEventListener('click', trigger.__awSearchClick);
      });

      if (this.searchPanel) {
        this.searchPanel.removeEventListener('mouseenter', this.boundHandleSearchPause);
        this.searchPanel.removeEventListener('mouseleave', this.boundHandleSearchResume);
        this.searchPanel.removeEventListener('focusin', this.boundHandleSearchPause);
        this.searchPanel.removeEventListener('focusout', this.boundHandleSearchResume);
      }

      this.drawerOpenButtons.forEach((button) => {
        button.removeEventListener('click', button.__awToggleDrawer);
      });

      this.drawerCloseButtons.forEach((button) => {
        button.removeEventListener('click', button.__awCloseDrawer);
      });

      this.drawerPanelButtons.forEach((button) => {
        button.removeEventListener('click', button.__awOpenPanel);
      });

      this.drawerBackButtons.forEach((button) => {
        button.removeEventListener('click', button.__awBackPanel);
      });

      this.root.removeEventListener('mouseleave', this.boundOnRootMouseLeave);
      document.removeEventListener('click', this.boundOnDocumentClick);
      document.removeEventListener('keydown', this.boundOnDocumentKeydown);
      window.removeEventListener('scroll', this.boundOnScroll);
      window.removeEventListener('resize', this.boundOnResize);
      this.desktopMedia.removeEventListener('change', this.boundOnMediaChange);
      document.body.style.overflow = '';
      this.clearScrollStopTimer();
      this.clearSearchCloseTimer();
      this.root.classList.remove('is-drawer-open', 'is-sticky-visible', 'is-mega-open');
      this.closeSearch();
    }

    bindDesktopMega() {
      this.navItems.forEach((item) => {
        const handler = () => {
          if (!this.desktopMedia.matches) {
            return;
          }

          const trigger = item.querySelector('[data-aw-header-trigger]');
          if (!trigger) {
            this.closeMega();
            return;
          }

          const target = trigger.getAttribute('data-aw-header-trigger');
          if (!target) {
            this.closeMega();
            return;
          }

          this.openMega(target, trigger);
        };

        item.__awNavEnter = handler;
        item.addEventListener('mouseenter', handler);
        item.addEventListener('focusin', handler);
      });

      this.root.addEventListener('mouseleave', this.boundOnRootMouseLeave);
    }

    bindSearch() {
      if (!this.searchPanel || !this.searchTriggers.length) {
        return;
      }

      this.searchTriggers.forEach((trigger) => {
        const handleEnter = () => {
          if (!this.desktopMedia.matches) {
            return;
          }

          this.openSearch();
        };

        const handleLeave = () => {
          if (!this.desktopMedia.matches) {
            return;
          }

          this.scheduleSearchClose();
        };

        const handleFocusIn = () => {
          if (!this.desktopMedia.matches) {
            return;
          }

          this.openSearch();
        };

        const handleFocusOut = (event) => {
          if (!this.desktopMedia.matches) {
            return;
          }

          this.handleSearchResume(event);
        };

        const handleClick = (event) => {
          if (!this.desktopMedia.matches) {
            const searchUrl = trigger.getAttribute('data-search-url');
            if (searchUrl) {
              window.location.href = searchUrl;
            }
            return;
          }

          event.preventDefault();

          if (this.searchPanel.classList.contains('is-active')) {
            this.closeSearch();
          } else {
            this.openSearch();
          }
        };

        trigger.__awSearchEnter = handleEnter;
        trigger.__awSearchLeave = handleLeave;
        trigger.__awSearchFocusIn = handleFocusIn;
        trigger.__awSearchFocusOut = handleFocusOut;
        trigger.__awSearchClick = handleClick;

        trigger.addEventListener('mouseenter', handleEnter);
        trigger.addEventListener('mouseleave', handleLeave);
        trigger.addEventListener('focusin', handleFocusIn);
        trigger.addEventListener('focusout', handleFocusOut);
        trigger.addEventListener('click', handleClick);
      });

      this.searchPanel.addEventListener('mouseenter', this.boundHandleSearchPause);
      this.searchPanel.addEventListener('mouseleave', this.boundHandleSearchResume);
      this.searchPanel.addEventListener('focusin', this.boundHandleSearchPause);
      this.searchPanel.addEventListener('focusout', this.boundHandleSearchResume);
    }

    bindDrawer() {
      this.drawerOpenButtons.forEach((button) => {
        const handler = () => {
          if (this.drawerOpen) {
            this.closeDrawer();
          } else {
            this.openDrawer();
          }
        };

        button.__awToggleDrawer = handler;
        button.addEventListener('click', handler);
      });

      this.drawerCloseButtons.forEach((button) => {
        const handler = () => this.closeDrawer();
        button.__awCloseDrawer = handler;
        button.addEventListener('click', handler);
      });

      this.drawerPanelButtons.forEach((button) => {
        const handler = () => {
          const panelId = button.getAttribute('data-aw-drawer-open');
          this.openDrawerPanel(panelId);
        };

        button.__awOpenPanel = handler;
        button.addEventListener('click', handler);
      });

      this.drawerBackButtons.forEach((button) => {
        const handler = () => this.resetDrawerPanels();
        button.__awBackPanel = handler;
        button.addEventListener('click', handler);
      });
    }

    bindGlobalEvents() {
      document.addEventListener('click', this.boundOnDocumentClick);
      document.addEventListener('keydown', this.boundOnDocumentKeydown);
      window.addEventListener('scroll', this.boundOnScroll, { passive: true });
      window.addEventListener('resize', this.boundOnResize);
      this.desktopMedia.addEventListener('change', this.boundOnMediaChange);
    }

    measure() {
      const announcementBar = document.querySelector('[data-aw-announcement-bar]');
      const announcementHeight = announcementBar ? announcementBar.offsetHeight : 0;
      const mainHeight = this.mainHeader ? this.mainHeader.offsetHeight : 0;
      const stickyHeight = this.stickyHeader ? this.stickyHeader.offsetHeight : 0;

      this.announcementHeight = announcementHeight;
      this.root.style.setProperty('--aw-header-main-offset', `${announcementHeight + mainHeight}px`);
      this.root.style.setProperty('--aw-header-sticky-offset', `${stickyHeight}px`);
    }

    getVisibleNavList() {
      if (this.root.classList.contains('is-sticky-visible') && this.stickyNavList) {
        return this.stickyNavList;
      }

      return this.mainNavList;
    }

    updateMegaWidth() {
      const visibleNav = this.getVisibleNavList();
      if (!visibleNav) {
        return;
      }

      const rect = visibleNav.getBoundingClientRect();
      const width = Math.max(0, rect.width);
      this.root.style.setProperty('--aw-header-mega-content-width', `${width}px`);
    }

    updateMegaAnchor(trigger, panel) {
      if (!trigger || !panel || !panel.classList.contains('aw-header-mega--below_one_column')) {
        return;
      }

      const inner = panel.querySelector('.aw-header-mega__inner');
      if (!inner) {
        return;
      }

      const previousHidden = panel.hidden;
      const previousAriaHidden = panel.getAttribute('aria-hidden');
      if (previousHidden) {
        panel.hidden = false;
        panel.setAttribute('aria-hidden', 'false');
      }

      const panelStyles = window.getComputedStyle(panel);
      const paddingLeft = Number.parseFloat(panelStyles.paddingLeft || '0') || 0;
      const paddingRight = Number.parseFloat(panelStyles.paddingRight || '0') || 0;
      const rect = trigger.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();
      const innerRect = inner.getBoundingClientRect();
      const contentWidth = Math.max(0, panelRect.width - paddingLeft - paddingRight);
      const desiredLeft = rect.left - panelRect.left - paddingLeft;
      const max = Math.max(0, contentWidth - innerRect.width);
      const clamped = Math.max(0, Math.min(max, desiredLeft));

      this.root.style.setProperty('--aw-header-mega-anchor-left', `${clamped}px`);

      if (previousHidden) {
        panel.hidden = true;
        panel.setAttribute('aria-hidden', previousAriaHidden || 'true');
      }
    }

    onRootMouseLeave() {
      if (!this.desktopMedia.matches) {
        return;
      }

      this.closeMega();
      this.scheduleSearchClose();
    }

    onDocumentClick(event) {
      if (!this.root.contains(event.target)) {
        this.closeMega();
        this.closeSearch();
        this.closeDrawer();
      }
    }

    onDocumentKeydown(event) {
      if (event.key !== 'Escape') {
        return;
      }

      this.closeMega();
      this.closeSearch();
      this.closeDrawer();
    }

    onScroll() {
      this.clearScrollStopTimer();
      this.scrollStopTimer = window.setTimeout(() => {
        this.updateSticky(false, true);
      }, 120);

      if (this.scrollTicking) {
        return;
      }

      this.scrollTicking = true;
      window.requestAnimationFrame(() => {
        this.updateSticky();
        this.scrollTicking = false;
      });
    }

    onResize() {
      this.measure();
      this.updateMegaWidth();
      this.updateSticky(true);

      if (this.activeMegaTarget) {
        const activeTrigger = this.triggers.find((trigger) =>
          trigger.getAttribute('data-aw-header-trigger') === this.activeMegaTarget &&
          trigger.offsetParent !== null
        );
        const activePanel = this.panels.find((panel) => panel.getAttribute('data-target') === this.activeMegaTarget);
        if (activeTrigger && activePanel) {
          this.updateMegaAnchor(activeTrigger, activePanel);
        }
      }
    }

    onMediaChange() {
      this.closeMega();
      this.closeSearch();
      this.closeDrawer();
      this.resetDrawerPanels();
      this.measure();
      this.updateMegaWidth();
    }

    clearScrollStopTimer() {
      if (this.scrollStopTimer) {
        window.clearTimeout(this.scrollStopTimer);
        this.scrollStopTimer = null;
      }
    }

    updateSticky(force = false, stopped = false) {
      const currentScrollY = window.scrollY;
      const scrollDelta = Math.abs(currentScrollY - this.lastScrollY);
      const wasVisible = this.root.classList.contains('is-sticky-visible');
      const mainHeaderRect = this.mainHeader ? this.mainHeader.getBoundingClientRect() : null;
      const isMainHeaderVisible = mainHeaderRect
        ? mainHeaderRect.bottom > this.announcementHeight
        : currentScrollY < 100;
      const shouldShowSticky = !this.drawerOpen && !isMainHeaderVisible;

      if (
        !force &&
        !stopped &&
        this.desktopMedia.matches &&
        scrollDelta > 0 &&
        (this.activeMegaTarget || this.searchPanel?.classList.contains('is-active'))
      ) {
        this.closeMega();
        this.closeSearch();
      }

      if (force || shouldShowSticky !== wasVisible) {
        this.root.classList.toggle('is-sticky-visible', shouldShowSticky);
        this.updateMegaWidth();

        if (this.activeMegaTarget) {
          const activeTrigger = this.triggers.find((trigger) =>
            trigger.getAttribute('data-aw-header-trigger') === this.activeMegaTarget &&
            trigger.offsetParent !== null
          );
          const activePanel = this.panels.find((panel) => panel.getAttribute('data-target') === this.activeMegaTarget);
          if (activeTrigger && activePanel) {
            this.updateMegaAnchor(activeTrigger, activePanel);
          }
        }
      }

      this.lastScrollY = currentScrollY;
    }

    openMega(target, trigger) {
      if (!target || !this.desktopMedia.matches) {
        return;
      }

      const panel = this.panels.find((item) => item.getAttribute('data-target') === target);
      if (!panel) {
        this.closeMega();
        return;
      }

      this.closeSearch();
      this.updateMegaWidth();
      this.activeMegaTarget = target;
      this.root.classList.add('is-mega-open');

      this.triggers.forEach((item) => {
        const isActive = item.getAttribute('data-aw-header-trigger') === target;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      });

      this.panels.forEach((item) => {
        const isActive = item.getAttribute('data-target') === target;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        item.hidden = !isActive;
      });

      this.updateMegaAnchor(trigger, panel);
    }

    closeMega() {
      this.activeMegaTarget = null;
      this.root.classList.remove('is-mega-open');

      this.triggers.forEach((trigger) => {
        trigger.classList.remove('is-active');
        trigger.setAttribute('aria-expanded', 'false');
      });

      this.panels.forEach((panel) => {
        panel.classList.remove('is-active');
        panel.setAttribute('aria-hidden', 'true');
        panel.hidden = true;
      });
    }

    openSearch() {
      if (!this.searchPanel || !this.desktopMedia.matches) {
        return;
      }

      this.clearSearchCloseTimer();
      this.closeMega();
      this.searchPanel.classList.add('is-active');
      this.searchPanel.setAttribute('aria-hidden', 'false');

      this.searchTriggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'true');
      });
    }

    closeSearch() {
      if (!this.searchPanel) {
        return;
      }

      this.clearSearchCloseTimer();
      this.searchPanel.classList.remove('is-active');
      this.searchPanel.setAttribute('aria-hidden', 'true');

      this.searchTriggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
      });
    }

    scheduleSearchClose() {
      this.clearSearchCloseTimer();
      this.searchCloseTimer = window.setTimeout(() => {
        if (!this.isSearchAreaActive()) {
          this.closeSearch();
        }
      }, 120);
    }

    clearSearchCloseTimer() {
      if (this.searchCloseTimer) {
        window.clearTimeout(this.searchCloseTimer);
        this.searchCloseTimer = null;
      }
    }

    handleSearchResume(event) {
      if (event.type === 'focusout' && event.relatedTarget && this.isWithinSearchArea(event.relatedTarget)) {
        return;
      }

      this.scheduleSearchClose();
    }

    isWithinSearchArea(node) {
      if (!node) {
        return false;
      }

      if (this.searchPanel && this.searchPanel.contains(node)) {
        return true;
      }

      return this.searchTriggers.some((trigger) => trigger.contains(node));
    }

    isSearchAreaActive() {
      const activeElement = document.activeElement;
      return this.isWithinSearchArea(activeElement);
    }

    openDrawer() {
      if (!this.drawer) {
        return;
      }

      this.closeMega();
      this.closeSearch();
      this.drawerOpen = true;
      this.root.classList.add('is-drawer-open');
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.drawerOpenButtons.forEach((button) => {
        button.setAttribute('aria-expanded', 'true');
        button.setAttribute('aria-label', 'メニューを閉じる');
      });
      document.body.style.overflow = 'hidden';
    }

    closeDrawer() {
      if (!this.drawer || !this.drawerOpen) {
        return;
      }

      this.drawerOpen = false;
      this.root.classList.remove('is-drawer-open');
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.drawerOpenButtons.forEach((button) => {
        button.setAttribute('aria-expanded', 'false');
        button.setAttribute('aria-label', 'メニューを開く');
      });
      this.resetDrawerPanels();
      document.body.style.overflow = '';
    }

    openDrawerPanel(panelId) {
      if (!panelId) {
        return;
      }

      this.drawerPanels.forEach((panel) => {
        const isActive = panel.getAttribute('data-aw-drawer-panel') === panelId;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
        this.setPanelTabIndex(panel, isActive);
      });
    }

    resetDrawerPanels() {
      this.drawerPanels.forEach((panel) => {
        const isActive = panel.getAttribute('data-aw-drawer-panel') === 'root';
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
        this.setPanelTabIndex(panel, isActive);
      });
    }

    setPanelTabIndex(panel, active) {
      panel.querySelectorAll('a, button, input').forEach((element) => {
        if (active) {
          element.removeAttribute('tabindex');
        } else {
          element.setAttribute('tabindex', '-1');
        }
      });
    }

    openBlock(blockId) {
      if (!blockId) {
        return;
      }

      const panel = this.panels.find((item) => item.getAttribute('data-block-id') === blockId);
      if (panel && this.desktopMedia.matches) {
        const activeTrigger = this.triggers.find((trigger) => trigger.getAttribute('data-aw-header-trigger') === panel.getAttribute('data-target'));
        this.openMega(panel.getAttribute('data-target'), activeTrigger);
        return;
      }

      const drawerPanel = this.drawerPanels.find((item) => item.getAttribute('data-block-id') === blockId);
      if (drawerPanel) {
        this.openDrawer();
        this.openDrawerPanel(drawerPanel.getAttribute('data-aw-drawer-panel'));
      }
    }
  }

  const init = (container = document) => {
    container.querySelectorAll('[data-aw-header]').forEach((root) => {
      if (instances.has(root)) {
        return;
      }

      const instance = new AWHeader(root);
      instance.init();
      instances.set(root, instance);
    });
  };

  const destroy = (container) => {
    if (!container) {
      return;
    }

    const root = container.matches?.('[data-aw-header]') ? container : container.querySelector?.('[data-aw-header]');
    if (!root) {
      return;
    }

    const instance = instances.get(root);
    if (!instance) {
      return;
    }

    instance.destroy();
    instances.delete(root);
  };

  document.addEventListener('DOMContentLoaded', () => {
    init(document);
  });

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroy(event.target);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest('[data-aw-header]');
    if (!root) {
      return;
    }

    const instance = instances.get(root);
    if (!instance) {
      return;
    }

    instance.openBlock(event.detail.blockId);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const root = event.target.closest('[data-aw-header]');
    if (!root) {
      return;
    }

    const instance = instances.get(root);
    if (!instance) {
      return;
    }

    instance.closeMega();
    instance.closeSearch();
    instance.closeDrawer();
  });
})();
